import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { hideModal as hideModalAction } from 'actions/modalActions';
import { useModalOffset } from 'hooks/useModalOffset';
import { Modal, Typography, Button, Layout } from '@hub-mono/ui';
import styled, { css } from 'styled-components';

const TitleContainer = styled(Layout)`
    min-height: 48px;
    width: 100%;
`;

const StyledModal = styled(Modal)`
    ${props => {
        if (props.fullWidth === 'xxs') {
            return css`
                .MuiPaper-root {
                    max-width: 350px;
                }
            `;
        }
    }}
`;

const ConfirmationModalNew = ({ title, message = '', onConfirm, additional }) => {
    const {
        withCancel = true,
        onReject,
        confirmButtonText = 'Yes, delete it',
        confirmButtonDataCy = 'confirm-modal-confirm',
        confirmButtonVariant = 'filled',
        cancelButtonText = 'Cancel',
        cancelActionOnHide,
        withEscButton,
        additionalButtons = [],
        topDivider = true,
        fullWidth = 'sm',
    } = additional;

    const dispatch = useDispatch();
    const offsetClassNames = useModalOffset();

    const hideModal = () => {
        dispatch(hideModalAction());
        cancelActionOnHide && onReject?.();
    };

    const reject = () => {
        hideModal();
        !cancelActionOnHide && onReject?.();
    };

    const confirm = () => {
        dispatch(hideModalAction());
        onConfirm && onConfirm();
    };

    const titleJSX = (
        <TitleContainer vAlign="center">
            {typeof title === 'string' ? (
                <Typography variant="label" size="xl">
                    {title}
                </Typography>
            ) : (
                title
            )}
        </TitleContainer>
    );

    const contentJSX = (
        <Layout stack>
            {message && typeof message === 'function' ? message() : <Typography>{message}</Typography>}
        </Layout>
    );
    const actionsJSX = (
        <Layout hAlign="flex-end" gap="--Spacing-150">
            {withCancel && (
                <Button variant="text" onClick={reject} dataCy="confirm-modal-cancel">
                    {cancelButtonText}
                </Button>
            )}
            {additionalButtons.map((button, index) => {
                return (
                    <Button
                        variant={button.variant ?? 'filled'}
                        key={index}
                        onClick={() => {
                            dispatch(hideModalAction());
                            button.onClick();
                        }}
                        dataCy={button.dataCy}
                    >
                        {button.text}
                    </Button>
                );
            })}
            {onConfirm && (
                <Button onClick={confirm} dataCy={confirmButtonDataCy} variant={confirmButtonVariant}>
                    {confirmButtonText}
                </Button>
            )}
        </Layout>
    );

    return (
        <StyledModal
            fullWidth={fullWidth}
            level="high"
            variant="dialog"
            topDivider={topDivider}
            bottomDivider={false}
            title={titleJSX}
            content={contentJSX}
            actions={actionsJSX}
            open
            onClose={reject}
            className={offsetClassNames}
            hideCloseButton={!withEscButton}
        />
    );
};

ConfirmationModalNew.propTypes = {
    title: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    onConfirm: PropTypes.func.isRequired,
    additional: PropTypes.shape({
        onReject: PropTypes.func,
        confirmButtonText: PropTypes.string,
        confirmButtonDataCy: PropTypes.string,
        cancelButtonText: PropTypes.string,
        withCancel: PropTypes.bool,
    }),
};

ConfirmationModalNew.defaultProps = {
    additional: {
        onReject: null,
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel',
        withCancel: true,
    },
};

export const StyledDialogTitleLayout = styled(Layout)`
    width: 100%;
`;

export default ConfirmationModalNew;
